import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhoneAlt,
  faMapMarkerAlt,
  faHandHoldingHand
} from '@fortawesome/free-solid-svg-icons';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const ZOCDOC_URL =
  'https://www.zocdoc.com/practice/hudson-river-osteopathy-167865?lock=true&isNewPatient=false&referrerType=widget';

const trackZocdocClick = (label) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'zocdoc_click', {
      event_category: 'engagement',
      event_label: label,
    });
  }
};

const BackgroundComponent = ({ activeSection, setActiveSection, currentIndex }) => {
  const theme = useTheme();

  const [menuOpen, setMenuOpen] = useState(false);
  const [bgHeight, setBgHeight] = useState('50vh');
  const [titleSize, setTitleSize] = useState('1.4rem');
  const [heroHeadlineSize, setHeroHeadlineSize] = useState('1.2rem');
  const [supportingTextSize, setSupportingTextSize] = useState('0.6rem');
  const [subtitleSize, setSubtitleSize] = useState('0.6rem');
  const [contactSize, setContactSize] = useState('0.6rem');

  useEffect(() => {
    const updateResponsiveValues = () => {
      const w = window.innerWidth;
      const aspectRatio = 9 / 16;
      const calculatedHeight = window.innerWidth * aspectRatio;
      const vhHeight = window.innerHeight * 0.5;
      const minHeight = 320;
      setBgHeight(`${Math.max(Math.min(calculatedHeight, vhHeight), minHeight)}px`);

      if (w < 400) {
        setHeroHeadlineSize('1.2rem');
        setTitleSize('1.1rem');
        setSupportingTextSize('0.65rem');
        setSubtitleSize('0.65rem');
        setContactSize('0.65rem');
      } else if (w < 500) {
        setHeroHeadlineSize('1rem');
        setTitleSize('1.1rem');
        setSupportingTextSize('0.6rem');
        setSubtitleSize('0.55rem');
        setContactSize('0.65rem');
      } else if (w < 600) {
        setHeroHeadlineSize('1.1rem');
        setTitleSize('1.2rem');
        setSupportingTextSize('0.7rem');
        setSubtitleSize('0.65rem');
        setContactSize('0.7rem');
      } else if (w < 700) {
        setHeroHeadlineSize('1.2rem');
        setTitleSize('1.3rem');
        setSupportingTextSize('0.8rem');
        setSubtitleSize('0.75rem');
        setContactSize('0.75rem');
      } else {
        setHeroHeadlineSize('1.3rem');
        setTitleSize('1.4rem');
        setSupportingTextSize('0.85rem');
        setSubtitleSize('0.8rem');
        setContactSize('0.85rem');
      }
    };

    updateResponsiveValues();
    window.addEventListener('resize', updateResponsiveValues);
    return () => window.removeEventListener('resize', updateResponsiveValues);
  }, []);
  
  const sharedBoxStyle = {
    backgroundColor: 'rgba(32, 32, 32, 0.55)',
    borderRadius: '10px',
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.3)',
    maxWidth: '100vw',
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: bgHeight,
        width: '100%',
        backgroundImage: 'url("/images/background.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflow: 'visible',
        fontFamily: 'Noto Sans, sans-serif',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'transparent', zIndex: 1 }} />

      {/* Title - Centered */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 6, md: 12 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            ...sharedBoxStyle,
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box>
            <Typography component="h1" sx={{ color: 'white', fontWeight: 'bold', fontSize: titleSize, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              Hudson River Osteopathy
            </Typography>
            <Typography sx={{ color: 'white', fontSize: subtitleSize, whiteSpace: 'nowrap' }}>
              Elena DiRusso, DO
            </Typography>
          </Box>
          <FontAwesomeIcon icon={faHandHoldingHand} style={{ fontSize: '1.5em', color: 'white' }} />
        </Box>
      </Box>

      {/* Burger Menu */}
      <Box
        component="button"
        type="button"
        aria-label="Open navigation menu"
        sx={{
          position: 'absolute',
          top: { xs: 6, md: 12 },
          right: 16,
          zIndex: 3,
          cursor: 'pointer',
          color: 'white',
          background: 'transparent',
          border: 0,
          p: 0,
        }}
        onClick={() => setMenuOpen(true)}
      >
        <MenuIcon fontSize="large" />
      </Box>

      {/* Headline */}
      <Box sx={{ position: 'absolute', top: '50%', left: 0, transform: 'translateY(-50%) translateX(-5%)', zIndex: 2 }}>
        <Box
          sx={{
            ...sharedBoxStyle,
            display: 'flex',
            flexDirection: 'row',
            width: '90vw',
            maxWidth: '400px',
            py: 1.5,
          }}
        >
          <Box sx={{ width: theme.spacing(2.5) }} />
          <Box sx={{ px: 1.5, display: 'flex', flexDirection: 'column' }}>
            <Typography component="p" sx={{ color: 'white', mb: 0.75, whiteSpace: 'pre-line', lineHeight: 1.3, fontSize: heroHeadlineSize }}>
              Holistic Healing,{"\n"}Tailored to You
            </Typography>
            <Typography sx={{ color: 'white', mb: 1, fontSize: supportingTextSize }}>
              Call or email to schedule an appointment today.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              component="a"
              href={ZOCDOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              onClick={() => trackZocdocClick('Schedule an Appointment')}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 'bold',
                px: 2,
                py: 0.5,
                fontSize: supportingTextSize,
                '&:hover': { backgroundColor: '#f2f2f2' },
              }}
            >
              Schedule an Appointment
            </Button>

            </Box>
          </Box>
        </Box>
      </Box>

      {/* Contact */}
      <Box sx={{ position: 'absolute', bottom: { xs: 8, sm: 16, md: 20 }, left: 10, transform: 'translateX(-15%)', zIndex: 2 }}>
        <Box
          sx={{
            ...sharedBoxStyle,
            display: 'flex',
            flexDirection: 'row',
            fontSize: contactSize,
            py: 1,
          }}
        >
          <Box sx={{ width: theme.spacing(3) }} />
          <Box sx={{ color: 'white', display: 'flex', flexDirection: 'column', gap: 0.5, px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <a href="tel:19145483534" style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', fontSize: contactSize }}>
                <FontAwesomeIcon icon={faPhoneAlt} style={{ marginRight: 10, fontSize: '1.2em' }} />
                (914) 548-3534
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <a
                href="https://www.google.com/maps/search/?api=1&query=55+Livingston+Ave,+Dobbs+Ferry,+NY"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', fontSize: contactSize }}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 10, fontSize: '1.2em' }} />
                55 Livingston Ave, Dobbs Ferry, NY
              </a>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Menu Overlay */}
      {menuOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            padding: 2,
          }}
        >
          <CloseIcon
            fontSize="large"
            sx={{ color: 'white', cursor: 'pointer' }}
            onClick={() => setMenuOpen(false)}
          />
          <Box
            sx={{
              marginTop: -1,
              width: '100%',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2.5,
            }}
          >
            {[
              { label: 'Home', onClick: () => { setActiveSection('omm'); setMenuOpen(false); } },
              { label: 'About', onClick: () => { setActiveSection('about'); setMenuOpen(false); } },
              { label: 'Conditions', onClick: () => { setActiveSection('conditions'); setMenuOpen(false); } },
              { label: 'Contact', onClick: () => { setMenuOpen(false); window.location.href = 'https://hushforms.com/hrosteopathy-secure-contact-form'; } },
              { label: 'Schedule', onClick: () => { trackZocdocClick('Schedule'); setMenuOpen(false); window.open(ZOCDOC_URL, '_blank', 'noopener,noreferrer'); } },
            ].map((item) => (
              <Typography
                key={item.label}
                variant="h6"
                onClick={item.onClick}
                sx={{
                  cursor: 'pointer',
                  width: '220px',          // <- consistent column width
                  textAlign: 'center',     // <- keeps text centered within that width
                  userSelect: 'none',
                }}
              >
                {item.label}
              </Typography>
            ))}
          </Box>

        </Box>
      )}
    </Box>
  );
};

export default BackgroundComponent;
